import axios from "axios";
import React, { useState, useRef, useEffect } from "react";
import { IS_MOBILE, WEB_PUSH_PUBLIC_KEY } from "../constants";
import { broadcaster, setBroadcastHookState } from "./events";
import { getCurrentUser } from "../app";
import { round } from "../utils";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Popup } from "../ui/popups";
import {screens} from 'tailwindcss/defaultTheme'
import { ViewPort } from "../constants";


function debounce(ctx, delay, func, key){
	key = key || "debounce";
	if(ctx[key]){
		clearTimeout(ctx[key]);
	}
	ctx[key] = setTimeout(func, delay);
}

var ExtToMimeType = {
	"jpg": "image/jpeg",
	"jpeg": "image/jpeg",
	"png": "image/png",
	"gif": "image/gif",
	"mp3": "audio/mp3",
	"wav": "image/wav",
	"ogg": "audio/ogg",
	"mp4": "video/mp4",
	"webp": "image/webp",
	"svg": "image/svg+xml",
	"pdf": "application/pdf"
}

function b64ToUint8Array(base64String) {
	const padding = '='.repeat((4 - base64String.length % 4) % 4);
	const base64 = (base64String + padding)
	  .replace(/\-/g, '+')
	  .replace(/_/g, '/');

	const rawData = window.atob(base64);
	const outputArray = new Uint8Array(rawData.length);

	for (let i = 0; i < rawData.length; ++i) {
	  outputArray[i] = rawData.charCodeAt(i);
	}
	return outputArray;
}


function isImage(file){
	if(file.mime_type){
		if(!file.mime_type.startsWith("image")){
			return false;
		}
		return file.mime_type;
	}
	let file_url = file.thumbnail || file.url || file;
	if(file_url && file_url.endsWith){
		let match = file_url.match(/\.(jpg|jpeg|png|gif|webp|svg)($|\?)/i);
		return match && ExtToMimeType[match[1]];
	}
	return false;
}

function isAudio(file){
	if(file.mime_type){
		if(file.mime_type){
			if(!file.mime_type.startsWith("audio")){
				return false;
			}
			return file.mime_type;
		}
	
		file = file.url;
	}
	let file_url = file.url || file;
	if(file_url && file_url.endsWith){
		let match = file_url.match(/\.(mp3|wav|ogg)($|\?)/i);
		return match && ExtToMimeType[match[1]];
	}
	return false;
}

function isVideo(file){
	if(file.mime_type){
		if(!file.mime_type.startsWith("video")){
			return false;
		}
		return file.mime_type;
	}
	let file_url = file.url || file;
	if(file_url && file_url.endsWith){
		let match = file_url.match(/\.(mp4)($|\?)/i);
		return match && ExtToMimeType[match[1]];
	}
	return false;
}

function isPdf(file) {
	if(file.mime_type){
		if(!file.mime_type.startsWith("pdf")){
			return false;
		}
		return file.mime_type;
	}
	let file_url = file.url || file;
	if(file_url && file_url.endsWith){
		let match = file_url.match(/\.(pdf)($|\?)/i);
		return match && ExtToMimeType[match[1]];
	}
	return false;
}

function getStartOfDay(date){
	date = date || new Date();
	return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

var _counter = 0;
function useRerender(){
	const [value, setValue] = useState(0);
	return () => setValue(++_counter);   
}

function useRerenderWithValue(){
	const [value, setValue] = useState(0);
	return [value, () => setValue(++_counter)];   
}


/* webpush */
navigator?.serviceWorker?.ready.then((service_worker_registration) => {
	// Do we already have a push message subscription?
	if(service_worker_registration.pushManager){
		setBroadcastHookState("web_push_support", true);   
		service_worker_registration.pushManager
			.getSubscription()
			.then((subscription) => {
				setBroadcastHookState("web_push_subscription", subscription);       
			});
	}
});

function requestWebPush(cb){
	navigator.serviceWorker.ready.then((service_worker_registration) => {
		service_worker_registration.pushManager.subscribe({
			userVisibleOnly: true,
			applicationServerKey: b64ToUint8Array(WEB_PUSH_PUBLIC_KEY)
		}).then(
			(subscription) => {
				setBroadcastHookState("web_push_subscription", subscription);       
			},
			(error) => {
				setBroadcastHookState("web_push_subscription", null);       
			}
		);
	});
}

/* common base endpoint for push */

broadcaster.add_event_listener("web_push_subscription", async (subscription) => {
	if(!subscription) return;
	let user = await getCurrentUser();
	let web_push_token = JSON.stringify(subscription);
	if(localStorage.getItem("WEB_PUSH_TOKEN_KEY") != `${user?._id}_${web_push_token}`){
		axios.post(
			"/update_push_token", 
			{
				"push_token": web_push_token,
				"os_type": "WEB_PUSH"
			}
		).then((resp) => {
			localStorage.setItem("WEB_PUSH_TOKEN_KEY", `${user?._id}_${web_push_token}`);
		});
	}
});

/* webpush end */

/* standalone app install */
var deferedPwaPrompt = null;
window.addEventListener('beforeinstallprompt', (e) => {
	e.preventDefault();
	deferedPwaPrompt = e;
	setBroadcastHookState('app_install_support', true);
});

async function requestAppInstall(){
	if(deferedPwaPrompt){
		//ask to install as  app
		deferedPwaPrompt.prompt();
		return await deferedPwaPrompt.userChoice; // {accepted, dismissed, not_supported}
	}
	return "not_supported";
}
const isInStandaloneMode = () =>
	  (window.matchMedia('(display-mode: standalone)').matches) || (window.navigator.standalone) || document.referrer.includes('android-app://');

/* standalone app install end */

function sanitizedWANumber(wa_number) {
	return wa_number.split('-')[0]
}

function openWhatsapp(wa_number, text = "") {
	let url = `https://wa.me/${sanitizedWANumber(wa_number)}/?text=${encodeURIComponent(text)}`
	IS_MOBILE
		? (window.location.href = url)
		: window.open(url, "_blank", "noreferrer");
}

/* standalone app install end */


/* some default events that fire automatically */
let visibilityChange = "visibilitychange"
if (document.mozHidden !== undefined) {      
    visibilityChange = "mozvisibilitychange";
} else if (document.msHidden !== undefined) {      
    visibilityChange = "msvisibilitychange";
} else if (document.webkitHidden !== undefined) {      
    visibilityChange = "webkitvisibilitychange";
} else if (document.oHidden !== undefined) {      
    visibilityChange = "ovisibilitychange";
}

document.addEventListener(visibilityChange, function(event) {
    broadcaster.broadcast_event(
        "is_page_visible",
        document.visibilityState == "visible"
    );
}, false);

/* resize */
function getScreensData() {
	return Object.entries(screens).reduce((result, [name, width]) => {
		const max_width = Number(width.split("px")[0]) - 1;

		const match_width = window.matchMedia(`(min-width: ${width})`);
		const match_max_width = window.matchMedia(`(max-width: ${max_width}px)`);

		result[name] = match_width?.matches;
		result[`max-${name}`] = match_max_width?.matches;

		return result;
	}, {});
}

// will be deregistered on window close
function onWindowResize() {
    setBroadcastHookState("tw_screen_size", getScreensData());
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty("--vh", `${vh}px`);
}

broadcaster.add_event_listener("viewport", onWindowResize);


/*js function to understand the screen size*/
function onResize() {
    let width = document.documentElement.clientWidth || document.body.clientWidth || window.innerWidth || 0;
	let height = document.documentElement.clientHeight || document.body.clientHeight || window.innerHeight || 0;
    if(width === ViewPort.WIDTH && height === ViewPort.HEIGHT) return; // on mobile it does trigger on scroll
    ViewPort.WIDTH = width;
    ViewPort.HEIGHT = height;
    setBroadcastHookState("viewport", {...ViewPort});
};
window.addEventListener('resize', onResize, false);  
(() => onResize())(); // first time resize call

const PendingPromise = {};
function loadGoogleMapsScript(api_key){
	if (window.google?.maps) {
		return Promise.resolve()
	}
	if(PendingPromise.gmaps){
		return PendingPromise.gmaps;
	}

	var resolver = null;
	let ret = PendingPromise.gmaps = new Promise((resolve, reject) => {resolver = resolve});
	const script = window.gmaps_script = document.createElement('script');
	window.initMap = () => {
		PendingPromise.gmaps = null;
		resolver();
	};
	script.src = `https://maps.googleapis.com/maps/api/js?key=${api_key}&libraries=places&callback=initMap&loading=async`;
	script.async = true;
	script.defer = true;
	document.body.appendChild(script);
	return ret;
}

function loadFbConnect(facebook_app_id){
	if(window.FB) return Promise.resolve();
	if(PendingPromise.fb_connect) return PendingPromise.fb_connect;

	var resolver = null;
	let ret = PendingPromise.fb_connect = new Promise((resolve, reject) => {resolver = resolve});
	window.fbAsyncInit = function () {
		// JavaScript SDK configuration and setup
		PendingPromise.fb_connect = null;
		window.FB.init({
			appId:    facebook_app_id, // Facebook App ID
			autoLogAppEvents: true,
			xfbml:    true, // parse social plugins on this page
			version:  'v25.0' //Graph API version
		});
		resolver();
	};

	// Load the JavaScript SDK asynchronously
	(function (d, s, id) {
	var js, fjs = d.getElementsByTagName(s)[0];
	if (d.getElementById(id)) return;
	js = d.createElement(s); js.id = id;
	js.src = "https://connect.facebook.net/en_US/sdk.js";
	fjs.parentNode.insertBefore(js, fjs);
	}(document, 'script', 'facebook-jssdk'));

	return ret
}

const _scipts_loaded = {}
function loadScript(script_url){
	if (_scipts_loaded[script_url]) {
		return Promise.resolve()
	}
	if(PendingPromise[script_url]){
		return PendingPromise[script_url];
	}

	var resolver = null;
	let ret = PendingPromise[script_url] = new Promise((resolve, reject) => {resolver = resolve});
	const script = document.createElement('script');
	script.src = script_url;
	script.async = true;
	script.defer = true;
	script.onload = () => {
		_scipts_loaded[script_url] = true;
		PendingPromise[script_url] = null;
		resolver();
	}
	document.body.appendChild(script);
	return ret;
}


function loadCss(css){
	if (_scipts_loaded[css]) {
		return Promise.resolve()
	}
	if(PendingPromise[css]){
		return PendingPromise[css];
	}
	var resolver = null;
	let ret = PendingPromise[css] = new Promise((resolve, reject) => {resolver = resolve});
	const link = document.createElement('link');
	link.href = css;
	link.rel = "stylesheet";
	link.type = "text/css";
	link.onload = () => {
		_scipts_loaded[css] = true;
		PendingPromise[css] = null;
		resolver();
	}
	document.head.appendChild(link);
	return ret;
}

function nearestElement(el, selector) {
	while(el && !el.matches(selector)){
		el = el.parentElement;
	}
	return el
}

/* shade -> 0, 256 */
function getRandomColor(str, shade=0, colors_list){
	str = str || new Date().getTime().toString();
	let hash = 0;
	for (let i = 0; i < str.length; i++) {
		hash = str.charCodeAt(i) + ((hash << 5) - hash);
	}
	
	if(colors_list?.length) return colors_list[Math.abs(hash) % colors_list.length];
	const s = (256 - shade) / 256;
	const r = parseInt(((hash & 0xFF0000) >> 16) * s + shade);
	const g = parseInt(((hash & 0x00FF00) >> 8) * s + shade);
	const b = parseInt((hash & 0x0000FF) * s  + shade);

	return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

const isValidEmail = (email_address) => {
	return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email_address);
}

/**
 * This function checks if a given phone number is valid.
 *
 * @param {string} phone_number - The phone number to be validated.
 * @returns {boolean} - Returns true if the phone number is valid, false otherwise.
 *
 * The function uses a regular expression to check if the phone number matches the pattern of 6 to 18 digits.
 * The pattern allows for an optional '+' at the beginning, indicating international phone numbers.
 *
 * @example
 * isValidPhone('+254712345678'); // Returns true
 * isValidPhone('0712345678'); // Returns true
 * isValidPhone('12345'); // Returns false
 */
function isValidPhone(phone_number) {
	return /^[0-9\+]{6,18}$/.test(phone_number);
}

const NON_ALPHA_GROUPS_REGEX = /[^0-9a-zA-Z]+/g;  // multiple non-alpha groups
const NON_ALPHA_REGEX = /[^0-9a-zA-Z]/g;  //
const sanitizeToUnderscoreId = (str) => str.replace(NON_ALPHA_GROUPS_REGEX, "_").replace(/_+$/, "").toLowerCase();
const sanitizeToId = (str) => str.replace(NON_ALPHA_REGEX, "").toLowerCase();
const sanitizeToTitle = (str) => str.replace(NON_ALPHA_GROUPS_REGEX, " ").replace(/\s+$/, "").trim();

const CURRENCY_SYMBOLS = {
	"KES": "KSh", "USD": "$", "INR":  "₹", "EUR": "€", "GBP": "£", "NGN": "₦", "RWF": "Rwf",
	"POINTS": "🪙", "ZAR": "R"
}

const CURRENCY_LOCALE = {
	"KES": "en-KE", "USD": "en-US", "INR": "en-IN", "EUR": "en-AL", "GBP": "en-GB", "NGN": "en-NG", "RWF": "en-RE",
	"POINTS": "en-US", "ZAR": "en-ZA"
}

const CURRENCY_DECIMALS = {'BHD':3, 'JOD':3, 'KWD':3, 'OMR':3, 'TND':3}

const currencyToSymbol = (currency) => CURRENCY_SYMBOLS[currency] || currency || "";
const currencyToLocale = (currency) => CURRENCY_LOCALE[currency] || "en-KE";

const defined = (...args) => args.find((arg) => arg !== undefined);
const nonNull = (...args) => args.find((arg) => arg !== undefined && arg !== null);

/* Intl add support for POINTS */
function getDisplayPrice (currency, price) {
	const formater_options = {
		style: "currency",
		currency: currency,
		minimumFractionDigits: CURRENCY_DECIMALS[currency] || 0,
	}
	try {
		return new Intl.NumberFormat(currencyToLocale(currency), formater_options).format(price / 1000)
	} catch (e) {
		return `${currencyToSymbol(currency)} ${getPrice(currency, price)}`
	}
}

function getPrice (currency, price) {
    if (CURRENCY_DECIMALS[currency] == 3) {
        return round(price / 1000, 3).toFixed(3)
    }
    return round(price / 1000)
}

function getPriceWithCurrency (currency, price) {
	return `${getPrice(currency, price)} ${currency}`
}

/* this triggers click listener on the currentTarget */
function doClickOnEnter(evt){
	if(evt.key === 'Enter' || evt.key === ' ') {
		evt.currentTarget.click();
	}
}


function nonNullObject(obj){
	let ret = {};
	for(let k in obj){
		if(obj[k] !== null && obj[k] !== undefined){
			ret[k] = obj[k];
		}
	}
	return ret;
}

function firstDefined(...args){
	for(let arg of args){
		if(arg !== undefined && arg !== null) return arg;
	}
	return undefined;
}

const ScrollContainer = ({ children, className = "", parent_classname = "" }) => {
	const scrollRef = useRef(null);
	const [show_left_arrow, setShowLeftArrow] = useState(false);
	const [show_right_arrow, setShowRightArrow] = useState(false);
	const [isHovered, setIsHovered] = useState(false);

	const checkScroll = () => {
		if (!scrollRef.current) return;

		const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
		const has_overflow = scrollWidth > clientWidth;

		setShowLeftArrow(has_overflow && scrollLeft > 5);
		setShowRightArrow(has_overflow && scrollLeft < scrollWidth - clientWidth - 5);
	};

	useEffect(() => {
		const timer = setTimeout(checkScroll, 100); // Small delay to ensure content is rendered
		window.addEventListener("resize", checkScroll);

		return () => {
			clearTimeout(timer);
			window.removeEventListener("resize", checkScroll);
		};
	}, [children]);

	const scroll = (direction) => {
		if (!scrollRef.current) return;
		const scrollAmount = 200;
		scrollRef.current.scrollBy({
			left: direction === "left" ? -scrollAmount : scrollAmount,
			behavior: "smooth",
		});
	};

	return (
		<div
			className={`tw-relative tw-flex-1 tw-min-w-0 ${parent_classname}`}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
		>
			{show_left_arrow && isHovered && (
				<button
					onClick={() => scroll("left")}
					className="tw-hidden md:tw-flex tw-absolute tw-left-0 tw-top-1/2 tw--translate-y-1/2 tw-z-10 tw-bg-[#d9d9d9] tw-shadow-lg tw-rounded-full tw-p-1.5 tw-items-center tw-justify-center"
					aria-label="Scroll left"
					type="button"
				>
					<ChevronLeft className="tw-w-4 tw-h-4 tw-text-black" />
				</button>
			)}

			<div
				ref={scrollRef}
				onScroll={checkScroll}
				className={className}
			>
				{children}
			</div>

			{show_right_arrow && isHovered && (
				<button
					onClick={() => scroll("right")}
					className="tw-hidden md:tw-flex tw-absolute tw-right-0 tw-top-1/2 tw--translate-y-1/2 tw-z-10 tw-bg-[#d9d9d9] tw-shadow-lg tw-rounded-full tw-p-1.5 tw-items-center tw-justify-center"
					aria-label="Scroll right"
					type="button"
				>
					<ChevronRight className="tw-w-4 tw-h-4 tw-text-black" />
				</button>
			)}
		</div>
	);
};

export {
	debounce, isImage, isAudio, isVideo, isPdf, getStartOfDay, openWhatsapp, useRerender, useRerenderWithValue,
	isInStandaloneMode, b64ToUint8Array, requestAppInstall, requestWebPush,
	loadGoogleMapsScript, nearestElement, getRandomColor, loadFbConnect, sanitizeToUnderscoreId, sanitizeToId,
	loadScript, loadCss, currencyToSymbol, CURRENCY_SYMBOLS, defined, isValidEmail, isValidPhone, currencyToLocale, firstDefined,
	getDisplayPrice, getPrice, getPriceWithCurrency, doClickOnEnter, sanitizeToTitle, nonNullObject, ScrollContainer, nonNull
};
